/**
 * The prescription viewer's transform math.
 *
 * Pure: measurements in, transform out. No DOM, no React, no time. The viewer
 * reads pointer and wheel events and element rects, then calls these.
 *
 * One transform is the single source of truth for three inputs that must not
 * fight each other: the discrete zoom control, the wheel (continuous, anchored
 * on the cursor), and drag-to-pan. `scale: 1` means "fits the viewport".
 *
 * Ported from V1's `features/quotation-detail/core/imagePanZoom.ts`. Almost
 * every constant below is the residue of a pharmacist using the real thing on
 * real photographs, so the reasoning is kept with the numbers -- these are not
 * values to re-derive from taste.
 */

export interface Transform {
  readonly scale: number
  readonly x: number
  readonly y: number
}

export interface Size {
  readonly w: number
  readonly h: number
}

/** Normalized 0..1, the way blocks and finding regions are stored. */
export interface NormalizedBox {
  readonly top: number
  readonly left: number
  readonly bottom: number
  readonly right: number
}

export const IDENTITY_TRANSFORM: Transform = { scale: 1, x: 0, y: 0 }

export const MIN_SCALE = 0.5

/*
 * The zoom ceiling, and why it is not a round number.
 *
 * V1 shipped a flat 400% cap and a pharmacist reported she could not get close
 * enough to read a doctor's handwriting. Measured in the browser, 400% did not
 * even reach the file's own resolution: a 1600px photograph painted into a
 * 640x520 column is drawn at 390x520, so one file pixel equals one screen pixel
 * only from 410% upward. At maximum zoom she was still looking at a *shrunken*
 * image. A 300 DPI PDF page reaches its true size at around 680%.
 *
 * So the ceiling is derived per image instead of fixed:
 *  - never below 800%, so even a small image opens up much further than before;
 *  - twice the file's true pixel size, because the ask was to get *closer* than
 *    1:1 -- magnifying the pixel is what makes bad handwriting legible;
 *  - never above 1600%, past which the raster layer is too large for the
 *    browser and buys no legibility, only stretched pixels.
 */
export const MIN_MAX_SCALE = 8
export const ABSOLUTE_MAX_SCALE = 16
export const PIXEL_REAL_HEADROOM = 2

/** The ceiling before the image's true size is known -- i.e. before it loads. */
export const MAX_SCALE = MIN_MAX_SCALE

/**
 * The zoom ceiling for one concrete image.
 *
 * `renderedWidth` is the width in screen pixels the image is *painted* at when
 * scale is 1 -- see `containedImageSize` -- not the width of its element. With
 * no measurement (zero or negative, which is the first frame and jsdom) this
 * returns the default rather than inventing a number from zero.
 */
export function maxScaleForImage(naturalWidth: number, renderedWidth: number): number {
  if (naturalWidth <= 0 || renderedWidth <= 0) return MAX_SCALE
  const pixelReal = naturalWidth / renderedWidth
  return Math.min(ABSOLUTE_MAX_SCALE, Math.max(MIN_MAX_SCALE, pixelReal * PIXEL_REAL_HEADROOM))
}

export function clampScale(
  scale: number,
  min: number = MIN_SCALE,
  max: number = MAX_SCALE,
): number {
  return Math.min(max, Math.max(min, scale))
}

/** Larger zooms faster per wheel notch. */
const WHEEL_ZOOM_SENSITIVITY = 0.0015

/** A wheel `deltaY` to a multiplicative factor: up zooms in, down zooms out. */
export function wheelDeltaToScaleFactor(deltaY: number): number {
  return Math.exp(-deltaY * WHEEL_ZOOM_SENSITIVITY)
}

/**
 * Zoom by `factor`, keeping the focal point stationary on screen.
 *
 * `fx`/`fy` are viewport-local pixels with the origin at its top-left, which is
 * what a wheel event's position gives you. Anchoring on the cursor is the
 * difference between zooming *into what you are looking at* and zooming into
 * the middle of the page and then hunting for it again.
 */
export function zoomAtPoint(
  t: Transform,
  factor: number,
  fx: number,
  fy: number,
  maxScale: number = MAX_SCALE,
): Transform {
  const scale = clampScale(t.scale * factor, MIN_SCALE, maxScale)
  const ratio = scale / t.scale
  return { scale, x: fx - (fx - t.x) * ratio, y: fy - (fy - t.y) * ratio }
}

export function panBy(t: Transform, dx: number, dy: number): Transform {
  return { scale: t.scale, x: t.x + dx, y: t.y + dy }
}

/** A discrete zoom percentage, recentred: picking a level resets the pan. */
export function transformForZoomPercent(percent: number, maxScale: number = MAX_SCALE): Transform {
  return { scale: clampScale(percent / 100, MIN_SCALE, maxScale), x: 0, y: 0 }
}

export function scaleToZoomPercent(scale: number): number {
  return Math.round(scale * 100)
}

export function toCssTransform(t: Transform): string {
  return `translate(${t.x}px, ${t.y}px) scale(${t.scale})`
}

/**
 * The size in screen pixels that `object-contain` paints an image at.
 *
 * This exists because measuring with `offsetWidth`/`offsetHeight` measures the
 * *element* -- the whole viewport -- and not the image. A 1600x2133 photograph
 * in a 640x520 column reports 640x520 while being painted at 390x520, and that
 * one confusion broke three things at once in V1: fit-to-width became a no-op,
 * the region overlay landed on the empty letterboxing beside the prescription
 * instead of on the prescription, and the zoom ceiling had no idea how many
 * real pixels existed.
 *
 * Null when a measurement is missing (any side zero or negative -- the first
 * frame, and jsdom), so the caller keeps its previous behaviour instead of
 * computing on top of zero.
 */
export function containedImageSize(natural: Size, container: Size): Size | null {
  if (natural.w <= 0 || natural.h <= 0 || container.w <= 0 || container.h <= 0) return null
  const ratio = Math.min(container.w / natural.w, container.h / natural.h)
  return { w: natural.w * ratio, h: natural.h * ratio }
}

/**
 * The opening transform: fill the width, aligned to the top.
 *
 * A portrait page fitted by its *height* opens with the text tiny -- "the image
 * is smaller", as the pharmacy put it. Filling the width and starting at the
 * top matches how a page is read; you drag down from there. A landscape page is
 * already width-bound under `contain`, so this is scale 1 and changes nothing.
 */
export function transformForFitWidth(imgSize: Size, containerSize: Size): Transform {
  const scale = clampScale(containerSize.w / Math.max(imgSize.w, 1))
  return {
    scale,
    x: imgSize.w * (0.5 - scale * 0.5),
    y: imgSize.h * 0.5 - containerSize.h / 2,
  }
}

/**
 * The resting transform: the whole page, centred.
 *
 * What the viewer shows when nothing is pinned. Filling the width was the
 * first answer -- see above -- but with the pins doing the zooming, a step
 * that has nothing to point at is better read as the page it is on, whole,
 * than as its top half. `transformForCropBox` takes over the moment a row is
 * chosen.
 */
export function transformForFitPage(imgSize: Size, containerSize: Size): Transform {
  const scale = clampScale(
    Math.min(containerSize.w / Math.max(imgSize.w, 1), containerSize.h / Math.max(imgSize.h, 1)),
  )
  return { scale, x: imgSize.w * (0.5 - scale * 0.5), y: 0 }
}

/**
 * The transform that frames a normalized box in the viewport.
 *
 * Display-only and never destructive: this is applied as an *initial* or
 * *stepped-to* transform, and every zoom, pan and drag afterwards is untouched.
 * A pharmacist can always zoom back out past it to the whole page, because
 * `clampScale` bounds the result like any other zoom.
 *
 * At scale 1 the wrapper is centred by its flex parent, so the box's centre --
 * in the wrapper's own untransformed pixels -- has to land on the wrapper's
 * centre after scaling from the origin.
 */
export function transformForCropBox(
  box: NormalizedBox,
  imgSize: Size,
  maxScale: number = MAX_SCALE,
  /*
   * The field the box has to fill. Without it the scale was worked out
   * against the image's own size -- right only when the image fills the
   * field, which a portrait page in a wide panel never does. The wider the
   * page got, the less a pin zoomed: "it has more space to zoom in".
   */
  containerSize: Size = imgSize,
): Transform {
  const boxWidthFrac = Math.max(box.right - box.left, 0.0001)
  const boxHeightFrac = Math.max(box.bottom - box.top, 0.0001)
  const scale = clampScale(
    Math.min(
      containerSize.w / (boxWidthFrac * Math.max(imgSize.w, 1)),
      containerSize.h / (boxHeightFrac * Math.max(imgSize.h, 1)),
    ),
    MIN_SCALE,
    maxScale,
  )

  const centerXFrac = (box.left + box.right) / 2
  const centerYFrac = (box.top + box.bottom) / 2

  return {
    scale,
    x: imgSize.w * (0.5 - scale * centerXFrac),
    y: imgSize.h * (0.5 - scale * centerYFrac),
  }
}

/**
 * Keeps the image from escaping the viewport.
 *
 * With a ceiling of 800% and more, unbounded dragging loses people: two
 * gestures and the prescription is gone, leaving a blank field and no clue
 * which way to come back. The rule is any image viewer's rule:
 *
 *  - image *larger* than the field: its edges never come inside, so there is
 *    always prescription under the cursor;
 *  - image *smaller* than the field: it stays wholly inside and cannot be
 *    pushed off an edge.
 *
 * Both fall out of the same interval, which is why there is one calculation.
 *
 * `imgSize` is the painted size at scale 1; `containerSize` is the visible
 * field. With no measurement of the field this returns `t` untouched: in jsdom
 * every rect is zero, and clamping against zero would invent an offset nobody
 * asked for.
 */
export function clampPan(t: Transform, imgSize: Size, containerSize: Size): Transform {
  if (containerSize.w <= 0 || containerSize.h <= 0) return t
  if (imgSize.w <= 0 || imgSize.h <= 0) return t

  // At scale 1 the flex parent centres the wrapper; the transform adds to that.
  const offsetX = (containerSize.w - imgSize.w) / 2
  const offsetY = (containerSize.h - imgSize.h) / 2

  const axis = (offset: number, side: number, container: number, value: number): number => {
    const flushStart = -offset
    const flushEnd = container - side * t.scale - offset
    const lo = Math.min(flushStart, flushEnd)
    const hi = Math.max(flushStart, flushEnd)
    return Math.min(hi, Math.max(lo, value))
  }

  return {
    scale: t.scale,
    x: axis(offsetX, imgSize.w, containerSize.w, t.x),
    y: axis(offsetY, imgSize.h, containerSize.h, t.y),
  }
}

/** One normalized region, as findings and blocks carry them. */
export interface RegionBox {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

/**
 * A region to frame, with breathing room around it.
 *
 * Zooming to the region's exact bounds puts the words flush against the edge of
 * the field, where they read as cropped. The margin is clamped so a region at
 * the edge of the page does not push the frame off it.
 */
export function regionZoomBox(box: RegionBox, margin = 0.03): NormalizedBox {
  return {
    left: Math.max(0, box.x - margin),
    top: Math.max(0, box.y - margin),
    right: Math.min(1, box.x + box.w + margin),
    bottom: Math.min(1, box.y + box.h + margin),
  }
}

/**
 * The single box covering every region of one finding.
 *
 * A finding cites blocks, not a rectangle -- a prescriber's name can be read
 * from two -- so what gets framed is their union. Regions on other pages are
 * the caller's problem: it decides which page to show first, and passes only
 * that page's.
 *
 * Undefined when there is nothing to frame, which is a real status: a citation
 * that failed validation leaves a finding with no region, and the viewer says
 * so rather than zooming confidently onto the wrong words.
 */
export function unionOfRegions(regions: readonly RegionBox[]): RegionBox | undefined {
  if (regions.length === 0) return undefined

  const left = Math.min(...regions.map((r) => r.x))
  const top = Math.min(...regions.map((r) => r.y))
  const right = Math.max(...regions.map((r) => r.x + r.w))
  const bottom = Math.max(...regions.map((r) => r.y + r.h))

  return { x: left, y: top, w: right - left, h: bottom - top }
}

export interface OverlayCssPosition {
  readonly left: string
  readonly top: string
  readonly width: string
  readonly height: string
}

/**
 * A normalized box as CSS percentages.
 *
 * Percentages rather than pixels because the overlay is a child of the same
 * wrapper the image is in: the box then lands correctly at any render size and
 * any zoom without either side knowing the other's checks.
 *
 * Rounded to four decimal places, because `0.07 * 100` is 7.000000000000001 and
 * that noise has no business being written into a style attribute.
 */
export function regionBoxToCss(box: RegionBox): OverlayCssPosition {
  const fmt = (n: number): string => `${parseFloat((n * 100).toFixed(4))}%`
  return { left: fmt(box.x), top: fmt(box.y), width: fmt(box.w), height: fmt(box.h) }
}

/*
 * The stepper's levels.
 *
 * Fixed steps up to 400%; above that they depend on how many pixels the image
 * actually has, so they are computed. Without this a PDF page (ceiling around
 * 1360%) becomes a twenty-click staircase.
 */
export const BASE_ZOOM_LEVELS = [50, 75, 100, 125, 150, 200, 300, 400] as const

/** Half a step per click above 400%. */
const HIGH_ZOOM_STEP_FACTOR = 1.5

const TOP_BASE_ZOOM_LEVEL: number = BASE_ZOOM_LEVELS[BASE_ZOOM_LEVELS.length - 1]!

/**
 * The stepper's levels for a ceiling of `maxPercent`.
 *
 * The ceiling always enters as the last level, so the last click reaches the
 * most this image allows. Levels within 5% of it are dropped: "1350%" then
 * "1360%" is a click with no visible effect.
 */
export function zoomLevelsFor(maxPercent: number): number[] {
  const ceiling = Math.round(maxPercent)
  const nearCeiling = ceiling * 0.95
  const levels: number[] = BASE_ZOOM_LEVELS.filter((level) => level < nearCeiling)

  let next = Math.round(TOP_BASE_ZOOM_LEVEL * HIGH_ZOOM_STEP_FACTOR)
  while (next < nearCeiling) {
    levels.push(next)
    next = Math.round(next * HIGH_ZOOM_STEP_FACTOR)
  }
  levels.push(ceiling)
  return levels
}

/**
 * The next level strictly above `current`, stopping at the ceiling.
 *
 * Compared against the true current value rather than the nearest level: the
 * wheel leaves the zoom on any number at all (237%), and rounding first made
 * "+" skip a whole step.
 */
export function zoomStepUp(current: number, levels: readonly number[]): number {
  return levels.find((level) => level > current + 0.5) ?? levels[levels.length - 1] ?? current
}

/** The next level strictly below `current`, stopping at the floor. */
export function zoomStepDown(current: number, levels: readonly number[]): number {
  for (let i = levels.length - 1; i >= 0; i -= 1) {
    const level = levels[i]!
    if (level < current - 0.5) return level
  }
  return levels[0] ?? current
}

export type RotationDegrees = 0 | 90 | 180 | 270

/** One click of the rotate button, all the way round. */
export function nextRotation(current: RotationDegrees): RotationDegrees {
  return ((current + 90) % 360) as RotationDegrees
}

/**
 * A drag delta from screen axes to the image's own, which turn with it.
 *
 * Without this, dragging right on an image rotated 90° moves it down.
 */
export function rotatePanDelta(
  rotation: RotationDegrees,
  dx: number,
  dy: number,
): { dx: number; dy: number } {
  switch (rotation) {
    case 90:
      return { dx: dy, dy: -dx }
    case 180:
      return { dx: -dx, dy: -dy }
    case 270:
      return { dx: -dy, dy: dx }
    default:
      return { dx, dy }
  }
}

/**
 * The visible field measured in the image's own axes.
 *
 * `rotatePanDelta` converts the *gesture*, but `clampPan` compares the image's
 * internal width against the field's width. At 90° or 270° those are swapped:
 * the screen's width now bounds the internal height. Without the swap a strip
 * of the prescription is unreachable at one end and an empty strip is allowed
 * at the other -- about 60px each side at production's checks. 180° swaps
 * nothing: the axes only change sign, and the clamp interval is symmetric.
 */
export function rotatedContainerSize(rotation: RotationDegrees, container: Size): Size {
  return rotation === 90 || rotation === 270
    ? { w: container.h, h: container.w }
    : { w: container.w, h: container.h }
}

/** Total pointer travel, in px, that still counts as a click rather than a drag. */
export const CLICK_DRAG_TOLERANCE_PX = 5

/**
 * Whether the pointer moved little enough between press and release to be a
 * click. The image pans, so without this threshold every drag ending on it
 * would read as a click and clear the highlight mid-read.
 */
export function isTapNotDrag(travelledPx: number): boolean {
  return travelledPx < CLICK_DRAG_TOLERANCE_PX
}
